import { computed, DestroyRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { GoogleAuthService } from '@lib/auth/data-access';
import { getGoogleAuthRedirectUrl } from '@lib/auth/utils';
import { TranslationService } from '@lib/i18n/data-access';
import {
  ACCESS_TOKEN_REFRESH_THRESHOLD_MILLISECONDS,
  AuthAppUserDto,
  AuthDto,
  LOCAL_STORAGE_AUTH_TOKEN_KEY,
  LOCAL_STORAGE_EXPIRE_TOKEN_KEY,
  LOCAL_STORAGE_REFRESH_TOKEN_KEY,
  LOCAL_STORAGE_TOKEN_KEY,
  SESSION_STORAGE_PROFILE_UPDATE_SUCCESS_KEY,
} from '@lib/user/utils';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { ModalService, OffCanvasService } from '@platform-workspace/design-system';
import { ToastService } from '@platform-workspace/design-system-v2';
import { UserScopes } from '@shared/utils/enums';
import { BaseResponse } from '@shared/utils/interfaces';
import { hideLoading, showLoading, withFeatureLoading } from '@shared/utils/models';
import { jwtDecode } from 'jwt-decode';
import { EMPTY, map, Observable, switchMap, tap } from 'rxjs';

import { UserApiService, UserTokenStateService } from '../../services';
import { UserState } from './user.interface';

const INITIAL_STATE: UserState = {
  token: null,
  authorities: [],
  refreshToken: null,
  tokenExpiry: null,
  authUser: null,
  scope: new Set(),
};

const decodeTokenScope = (token: string): Set<UserScopes> => {
  try {
    return new Set(Object.values<UserScopes>(jwtDecode<any>(token).scope).flat());
  } catch {
    return new Set<UserScopes>();
  }
};

const isTokenExpiring = (expiry: number | null): boolean =>
  expiry !== null && expiry - Date.now() <= ACCESS_TOKEN_REFRESH_THRESHOLD_MILLISECONDS;

const readAuthStateFromStorage = (): Pick<UserState, 'token' | 'refreshToken' | 'tokenExpiry' | 'scope'> => {
  const token = localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY);
  const refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
  const tokenExpiry = localStorage.getItem(LOCAL_STORAGE_EXPIRE_TOKEN_KEY);

  return {
    token,
    refreshToken,
    tokenExpiry: tokenExpiry ? parseInt(tokenExpiry, 10) : null,
    scope: token ? decodeTokenScope(token) : new Set<UserScopes>(),
  };
};

export const UserStore = signalStore(
  {
    providedIn: 'root',
  },
  withState(INITIAL_STATE),
  withFeatureLoading(),
  withComputed(({ token, authUser }) => ({
    isLoggedIn: computed(() => Boolean(token())),
    username: computed(() => (authUser() ? `${authUser()!.firstName} ${authUser()!.lastName}` : '')),
    dateFormat: computed(() => authUser()?.dateFormat),
    timeFormat: computed(() => authUser()?.timeFormat),
  })),
  withMethods(
    (
      store,
      googleAuthService = inject(GoogleAuthService),
      userApiService = inject(UserApiService),
      userTokenStateService = inject(UserTokenStateService),
      router = inject(Router),
      translationService = inject(TranslationService),
      modalService = inject(ModalService),
      offCanvasService = inject(OffCanvasService),
    ) => {
      const _authLoginComplete = (payload: AuthDto): void => {
        patchState(store, {
          token: payload.accessToken,
          refreshToken: payload.refreshToken,
          tokenExpiry: jwtDecode<{ exp: number }>(payload.accessToken).exp * 1000,
          scope: decodeTokenScope(payload.accessToken),
        });

        localStorage.setItem(LOCAL_STORAGE_AUTH_TOKEN_KEY, payload.accessToken);
        localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY, payload.refreshToken);
        localStorage.setItem(LOCAL_STORAGE_EXPIRE_TOKEN_KEY, `${jwtDecode<{ exp: number }>(payload.accessToken).exp * 1000}`);
      };

      const _refreshAccessTokenRaw = (): Observable<BaseResponse<AuthDto>> =>
        userApiService.refreshToken(localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY) ?? store.refreshToken()!).pipe(
          tap(({ payload }) => {
            _authLoginComplete(payload);
          }),
        );

      // Runs inside the navigator.locks callback. If another tab already refreshed the token
      // while this tab waited for the lock, sync the store from storage and return the fresh
      // token so the caller can skip a redundant refresh request. Returns null otherwise.
      // The store is patched BEFORE the token is returned so that requests unblocked via
      // waitUntilRefreshEnds() read the up-to-date token from the store.
      const _resolveFreshToken = (): string | null => {
        const state = readAuthStateFromStorage();

        if (state.token && !isTokenExpiring(state.tokenExpiry)) {
          patchState(store, state);
          return state.token;
        }

        return null;
      };

      return {
        isRefreshTokenAllowed(): boolean {
          return isTokenExpiring(store.tokenExpiry());
        },

        loginViaGoogle(): void {
          googleAuthService.login();
        },

        logout(redirectUrl = '/login'): void {
          modalService.dismissAll();
          offCanvasService.dismiss();
          localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
          localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN_KEY);
          localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
          localStorage.removeItem(LOCAL_STORAGE_EXPIRE_TOKEN_KEY);
          patchState(store, {
            token: null,
            refreshToken: null,
            tokenExpiry: null,
            authUser: null,
            authorities: [],
            scope: new Set(),
          });
          router.navigate([redirectUrl]);
        },

        loginViaAuthService(authCode: string): Observable<boolean> {
          return userApiService.loginViaAuth(authCode, getGoogleAuthRedirectUrl(window)).pipe(
            tap(({ payload }) => {
              _authLoginComplete(payload);
            }),
            switchMap(() => this.initUserViaAuth()),
            map(() => true),
          );
        },
        // `force` bypasses the cross-tab short-circuit so the refresh request always runs.
        // Use it after actions that change the token's claims/scopes (e.g. creating an operator
        // or updating a user) where the current token is still valid but stale. The default
        // (opportunistic) path lets a refresh already performed by another tab satisfy the call.
        refreshAccessToken(force = false): Observable<string | null> {
          return userTokenStateService.triggerRefresh(_refreshAccessTokenRaw, force ? undefined : _resolveFreshToken);
        },
        initUserViaAuth(): Observable<BaseResponse<AuthAppUserDto>> {
          if (!store.isLoggedIn()) {
            return EMPTY;
          }

          return userApiService.initUserViaAuth().pipe(
            tapResponse({
              next: ({ payload }) => {
                patchState(store, {
                  authUser: payload,
                });
              },
              error: console.error,
            }),
          );
        },
        updateUserProfile(userInfo: Partial<AuthAppUserDto>): Observable<void> {
          patchState(store, showLoading());
          return userApiService.updateUserViaAuth(userInfo).pipe(
            tapResponse({
              next: () => {
                sessionStorage.setItem(SESSION_STORAGE_PROFILE_UPDATE_SUCCESS_KEY, translationService.translate('profile.updateSuccess'));
              },
              error: console.error,
              finalize: () => {
                patchState(store, hideLoading());
              },
            }),
          );
        },
      };
    },
  ),
  withHooks({
    onInit(store) {
      const toastService = inject(ToastService);
      const destroyRef = inject(DestroyRef);

      const profileUpdateMessage = sessionStorage.getItem(SESSION_STORAGE_PROFILE_UPDATE_SUCCESS_KEY);
      if (profileUpdateMessage) {
        sessionStorage.removeItem(SESSION_STORAGE_PROFILE_UPDATE_SUCCESS_KEY);
        toastService.showSuccess(profileUpdateMessage);
      }

      const applyStorageState = (): void => {
        patchState(store, readAuthStateFromStorage());
      };

      const authStorageKeys = [LOCAL_STORAGE_AUTH_TOKEN_KEY, LOCAL_STORAGE_REFRESH_TOKEN_KEY, LOCAL_STORAGE_EXPIRE_TOKEN_KEY];

      const onStorage = (event: StorageEvent): void => {
        if (event.key !== null && !authStorageKeys.includes(event.key)) {
          return;
        }

        if (store.isLoggedIn() && !localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN_KEY)) {
          store.logout();
          return;
        }

        applyStorageState();
      };

      applyStorageState();
      window.addEventListener('storage', onStorage);
      destroyRef.onDestroy(() => window.removeEventListener('storage', onStorage));
    },
  }),
);
